// DropdownProvider.tsx
import React, { useState, ReactNode, useEffect } from 'react';
import { apiConfiguration } from '../apiConfig';
import DropdownContext, { ITenant } from './tenantContext';
import { RoleDTO, TenantDTO, TenantsApi } from '../data';
import { SignOutButton } from '../components/SignOutButton';
import { useLocation } from 'react-router-dom';
import { SingleCard } from '../layouts';
import { LoadIndicator, LoadPanel } from 'devextreme-react';

interface TenantProviderProps {
  children: ReactNode;
}

const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<ITenant>();
  const [tenants, setTenants] = useState<ITenant[]>([]);
  const [error, setError] = useState<string>();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      var api = new TenantsApi(apiConfiguration);
      const tenants = await api.getTenants(); // get all tenants

      if (tenants.length === 0) {
        setError('Nessun ambiente disponibile');
        return;
      }

      setTenants(tenants.map(t => ConvertTo(t)));

      var json = sessionStorage.getItem('ges.selectedTenant');
      if (json) {
        const obj = JSON.parse(json);

        if (obj.Id && tenants.some(t => t.id === obj.Id)) {  // check if the tenant is still available
          setCurrentTenant({...obj, IsInRole: (role: string) => IsInRole(obj.Roles, role)});  // set the tenant from session storage
          return;
        }
      }

      if (tenants.length > 0) {
        const { tenantId } = location.state || {};

        if (tenantId && tenants.some(t => t.id === tenantId)) {
          var t = tenants.find(t => t.id === tenantId);
          setCurrentTenant(ConvertTo(t!));  // set the tenant from the location state 
        }
        else  // set the first tenant as default
          setCurrentTenant(ConvertTo(tenants[0]));
      }
    }

    fetchData()
      .catch(e => {
        setError('Errore durante l\'accesso agli ambienti');
        console.error(e);
      });
  }, []);

  useEffect(() => {
    if (currentTenant) {
      sessionStorage.setItem('ges.selectedTenant', JSON.stringify(currentTenant));
    }
  }, [currentTenant]);

  function IsInRole(roles:string[], role: string): boolean {
    return roles.includes(role) || false;
  } 

  function ConvertTo(tenant: TenantDTO): ITenant {
    return {
      Id: String(tenant.id),
      Name: String(tenant.name),
      Roles: tenant.roles?.map(role => String(role.id)) || [],
      IsInRole: (role: string) => IsInRole(tenant.roles?.map(role => String(role.id)) || [], role)
    };
  }

  function setCurrentTenantById(tenantId: string) {
    const tenant = tenants.find(t => t.Id === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
    }
  }

  if (error)
    return (
      <SingleCard title={error} titleStyle={{ fontSize: '20px' }} >
        <SignOutButton />
      </SingleCard>
    );
  else if (currentTenant)
    return (
      <DropdownContext.Provider value={{ currentTenant, setCurrentTenantById, tenants }}>
        {children}
      </DropdownContext.Provider>
    );
  else
    return (
      <SingleCard title="Caricamento ambienti in corso ..." titleStyle={{ display: 'flex', fontSize: '20px', justifyContent: 'center' }} >
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
          <LoadIndicator visible={true} />
        </div>
      </SingleCard>
    );
};

export default TenantProvider;
